import i18n from '@/plugins/vue-i18n';
import { RouteRecordRaw } from 'vue-router';
import { Actions, Subjects } from '@boqnet/permissions';
import { useCan } from '@/composables/use-can';
import { usePaymentsSurfaceAccess } from '@/composables/use-payments-surface-access';
import { selectedOrgId } from '@/common/utils/role.utils';

const { t } = i18n.global;

export default [
  // Accounts
  {
    path: '/:organisationId?/accounts',
    component: () => import('@/views/accounts/accounts-view/AccountsLayout.vue'),
    meta: {
      title: t('routes.accounts.accountsPageTitle'),
      layout: 'default',
      requiresAuth: true,
      // Allow navigation if the user can access either tab — each tab gates itself independently.
      anyOf: [
        { action: Actions.LIST, subject: Subjects.ACCOUNTS_PAYABLE },
        { action: Actions.LIST, subject: Subjects.PAYMENT_REQUEST },
      ],
    },
    children: [
      // Accounts Payable Tab
      {
        path: 'accounts-payable',
        name: 'accounts-payable',
        component: () => import('@/views/accounts/accounts-view/tabs/AccountsPayableModule.vue'),
      },
      // Payment Requests Tab
      {
        path: 'payment-requests',
        name: 'payment-requests',
        component: () => import('@/views/accounts/accounts-view/tabs/PaymentRequestsModule.vue'),
      },
      // Payments Tab (PE-1004): every payment in the organisation, and the proofs that matched none.
      {
        path: 'payments',
        component: () => import('@/views/accounts/accounts-view/tabs/PaymentsModule.vue'),
        meta: {
          // Replaces the parent's AP-or-PR `anyOf` for both children, as `credits` does.
          // `router/index.ts` checks it once the ability is initialised; `beforeEnter` adds the
          // flag, which meta cannot express (DL-152).
          anyOf: [{ action: Actions.LIST, subject: Subjects.PAYMENTS_SURFACE }],
        },
        /**
         * Context: registered whatever the flag says, so the URL is reachable even when the strip
         * hides the tab. The flag and the ability are enforced here through `useCan()`, the Bulk
         * Orders tab precedent.
         * Runs once on entering the surface; moving between its two content tabs does not re-run
         * it, and `PaymentsModule` leaves if access is refused after it.
         */
        beforeEnter: () => {
          const { isResolved, canSeePaymentsSurface } = usePaymentsSurfaceAccess();

          // A cold load arrives before LaunchDarkly and the role refresh settle. It is let
          // through, and `PaymentsModule` renders and requests nothing until access is known,
          // then leaves if it is refused (DL-167 D17).
          if (!isResolved.value) return true;

          if (!canSeePaymentsSurface.value) {
            return {
              name: 'no-permissions',
              params: selectedOrgId.value ? { organisationId: selectedOrgId.value } : {},
            };
          }

          return true;
        },
        children: [
          {
            path: '',
            name: 'payments',
            component: () =>
              import('@/views/accounts/accounts-view/tabs/payments/PaymentsListTab.vue'),
          },
          {
            path: 'unmatched-proofs',
            name: 'payments-unmatched-proofs',
            component: () =>
              import('@/views/accounts/accounts-view/tabs/payments/UnmatchedProofsTab.vue'),
          },
        ],
      },
      // Credits Tab — shown to non-AP roles that retain credit/refund access (PE-213).
      // Org-scoped credits list (BE scopes to the user's sites/projects).
      {
        path: 'credits',
        name: 'accounts-credits',
        // Guard the route itself (not just the hidden tab) so direct navigation to
        // /accounts/credits is blocked for users without credit access. `anyOf`
        // overrides the parent's AP-or-PR `anyOf` (the router guard prefers `anyOf`
        // over action/subject), requiring LIST:CREDIT here specifically. (PE-213)
        meta: {
          anyOf: [{ action: Actions.LIST, subject: Subjects.CREDIT }],
        },
        component: () => import('@/views/accounts/accounts-view/tabs/CreditsModule.vue'),
      },
      {
        path: '',
        name: 'accounts',
        // Default-tab selection. Land on Accounts Payable only when the user can
        // actually view it; otherwise the Payment Requests tab, which is always
        // visible and never shows a no-permission state. `can(LIST:ACCOUNTS_PAYABLE)`
        // is authoritative: the roles that lost AP under the V2 cleanup (PE-213) fall
        // through to Payment Requests instead of an AP no-permission page. `useCan()`
        // is Pinia-only, so it resolves correctly in a route redirect. (PE-213, PE-460)
        redirect: () => {
          const { can } = useCan();
          return {
            name: can(Actions.LIST, Subjects.ACCOUNTS_PAYABLE)
              ? 'accounts-payable'
              : 'payment-requests',
          };
        },
      },
    ],
  },
  // Account Details
  {
    path: '/:organisationId?/accounts/:id/account-details',
    component: () => import('@/views/accounts/account-details-view/AccountDetailsLayout.vue'),
    meta: {
      title: t('routes.accounts.accountDetailsPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.LIST,
      subject: Subjects.ACCOUNTS_PAYABLE,
    },
    children: [
      // Payment Requests Tab
      {
        path: 'payment-requests',
        name: 'account-details-payment-requests',
        component: () => import('@/views/accounts/accounts-view/tabs/PaymentRequestsModule.vue'),
      },
      // Payments Tab: this vendor's payments, behind the payments surface's flag and ability, which
      // `VendorPaymentsModule` enforces as `PaymentsModule` does, since route meta cannot read a flag.
      {
        path: 'payments',
        name: 'account-details-payments',
        component: () =>
          import('@/views/accounts/account-details-view/tabs/VendorPaymentsModule.vue'),
      },
      // Orders Tab
      {
        path: 'orders',
        name: 'account-details-orders',
        component: () => import('@/views/accounts/account-details-view/tabs/OrdersModule.vue'),
      },
      // Claims Tab
      {
        path: 'claims',
        name: 'account-details-claims',
        component: () => import('@/views/accounts/account-details-view/tabs/ClaimsModule.vue'),
      },
      // Returns Tab
      {
        path: 'returns',
        name: 'account-details-returns',
        component: () => import('@/views/accounts/account-details-view/tabs/ReturnsModule.vue'),
      },
      // Credits Tab
      {
        path: 'credits',
        name: 'account-details-credits',
        component: () => import('@/views/accounts/account-details-view/tabs/CreditsModule.vue'),
      },
      // Statements Tab
      {
        path: 'statements',
        name: 'account-details-statements',
        component: () => import('@/views/accounts/account-details-view/tabs/StatementsModule.vue'),
      },
      // Transaction Log Tab
      {
        path: 'transaction-log',
        name: 'account-details-transaction-log',
        component: () =>
          import('@/views/accounts/account-details-view/tabs/TransactionLogModule.vue'),
      },
      {
        path: '',
        redirect: { name: 'account-details-payment-requests' },
      },
    ],
  },
] as RouteRecordRaw[];
